/**
 * Folding of T-SQL code: the line ranges the editor offers to collapse.
 *
 * Monaco folds a language either by the indentation of its lines, or by the ranges of registered providers - never
 * both, and the `#region` markers of the language configuration apply only to the indentation. The ranges below
 * therefore hold both kinds: the syntax ones (BEGIN ... END, CASE ... END, multi-line parentheses and block comments,
 * `-- #region` ... `-- #endregion` like the v1 editor) and the indentation ones Monaco would offer on its own.
 */

/** collapsed comment or `#region`; ranges of code have no kind */
export type CodeFoldingKind = 'comment' | 'region';

/** 1-based lines; the end line is collapsed too (Monaco FoldingRange) */
export type CodeFoldingRange = {
	start: number,
	end: number,
	kind?: CodeFoldingKind,
};

/** markers of the v1 editor (and of the rpsql language of the VS Code extension), matched at the start of a comment */
const REGION_START = /^(?:--|\/\*)\s*#region/;
const REGION_END = /^(?:--|\/\*)\s*#endregion/;

/** BEGIN followed by these words is a statement (BEGIN TRAN, BEGIN DIALOG ...), not a block */
const BEGIN_STATEMENTS = new Set(['tran', 'transaction', 'distributed', 'dialog', 'conversation']);
/** END followed by these words is a statement (END CONVERSATION), not the end of a block */
const END_STATEMENTS = new Set(['conversation']);

const WORD_START = /[\p{L}_@#]/u;
const WORD_PART = /[\p{L}\p{N}_@#$]/u;
const NEXT_WORD = /^\s*([\p{L}_]+)/u;

type Opener = { type: 'block' | 'paren', line: number };

/**
 * Folding ranges of the content. Keywords and brackets inside strings, quoted identifiers and comments are skipped;
 * a block, parentheses or region left open (code being written) is not folded. The line of END or of the closing
 * parenthesis stays visible when it starts with it, like the closing line of an indented block does.
 *
 * @param tabSize width of a tab for the indentation ranges (the model option)
 */
export function findSqlFoldingRanges(content: string, tabSize = 4): CodeFoldingRange[] {
	if (!content) return [];

	const ranges: CodeFoldingRange[] = [];
	const open: Opener[] = [];
	const regions: number[] = [];
	const n = content.length;
	let line = 1;
	let i = 0;

	const firstOnLine = (offset: number) => {
		let k = offset - 1;
		while (k >= 0 && (content[k] === ' ' || content[k] === '\t')) k--;
		return k < 0 || content[k] === '\n' || content[k] === '\r';
	};

	const nextWord = (offset: number) => NEXT_WORD.exec(content.slice(offset, offset + 64))?.[1].toLowerCase() ?? '';

	/** closes the nearest opener of the type; the openers above it were left unclosed */
	const close = (type: Opener['type'], offset: number) => {
		let index = open.length - 1;
		while (index >= 0 && open[index].type !== type) index--;
		if (index < 0) return;
		const start = open[index].line;
		open.length = index;
		const end = firstOnLine(offset) ? line - 1 : line;
		if (end > start) ranges.push({ start, end });
	};

	/** handles a region marker of a comment starting at the line; true when the comment is one */
	const region = (comment: string, start: number) => {
		if (REGION_START.test(comment)) {
			regions.push(start);
			return true;
		}
		if (REGION_END.test(comment)) {
			const begin = regions.pop();
			if (begin !== undefined && line > begin) ranges.push({ start: begin, end: line, kind: 'region' });
			return true;
		}
		return false;
	};

	while (i < n) {
		const c = content[i];
		if (c === '\n') {
			line++;
			i++;
		} else if (c === '-' && content[i + 1] === '-') {
			let end = content.indexOf('\n', i);
			if (end < 0) end = n;
			region(content.slice(i, end), line);
			i = end;
		} else if (c === '/' && content[i + 1] === '*') {
			// block comments nest in T-SQL
			const start = line;
			let depth = 0;
			let k = i;
			while (k < n) {
				if (content[k] === '/' && content[k + 1] === '*') {
					depth++;
					k += 2;
				} else if (content[k] === '*' && content[k + 1] === '/') {
					k += 2;
					if (--depth === 0) break;
				} else {
					if (content[k] === '\n') line++;
					k++;
				}
			}
			if (!region(content.slice(i, k), start) && line > start) ranges.push({ start, end: line, kind: 'comment' });
			i = k;
		} else if (c === "'" || c === '"' || c === '[') {
			// a doubled closing character is an escaped one
			const quote = c === '[' ? ']' : c;
			let k = i + 1;
			while (k < n) {
				if (content[k] === quote) {
					if (content[k + 1] !== quote) break;
					k++;
				} else if (content[k] === '\n') {
					line++;
				}
				k++;
			}
			i = k + 1;
		} else if (c === '(') {
			open.push({ type: 'paren', line });
			i++;
		} else if (c === ')') {
			close('paren', i);
			i++;
		} else if (WORD_START.test(c)) {
			let k = i + 1;
			while (k < n && WORD_PART.test(content[k])) k++;
			// a column named like a keyword (t.end) is no keyword
			if (content[i - 1] !== '.') {
				const word = content.slice(i, k).toLowerCase();
				if (word === 'case' || (word === 'begin' && !BEGIN_STATEMENTS.has(nextWord(k)))) {
					open.push({ type: 'block', line });
				} else if (word === 'end' && !END_STATEMENTS.has(nextWord(k))) {
					close('block', i);
				}
			}
			i = k;
		} else {
			i++;
		}
	}

	return nest([...ranges, ...findIndentRanges(content.split(/\r?\n/), tabSize)]);
}

/** Indentation ranges the way Monaco computes them without a provider (indentRangeProvider, not off-side). */
function findIndentRanges(lines: string[], tabSize: number): CodeFoldingRange[] {
	const ranges: CodeFoldingRange[] = [];
	const previous = [{ indent: -1, endAbove: lines.length + 1 }];
	for (let line = lines.length; line > 0; line--) {
		const indent = indentLevel(lines[line - 1], tabSize);
		if (indent < 0) continue;
		let top = previous[previous.length - 1];
		if (top.indent > indent) {
			do {
				previous.pop();
				top = previous[previous.length - 1];
			} while (top.indent > indent);
			if (top.endAbove - 1 > line) ranges.push({ start: line, end: top.endAbove - 1 });
		}
		if (top.indent === indent) top.endAbove = line;
		else previous.push({ indent, endAbove: line });
	}
	return ranges;
}

/** Visible width of the leading whitespace, -1 for a blank line (Monaco computeIndentLevel). */
function indentLevel(text: string, tabSize: number) {
	let indent = 0;
	let i = 0;
	for (; i < text.length; i++) {
		if (text[i] === ' ') indent++;
		else if (text[i] === '\t') indent = indent - indent % tabSize + tabSize;
		else break;
	}
	return i === text.length ? -1 : indent;
}

/**
 * Keeps one range per start line and drops the ranges crossing another one, which Monaco would show as nonsense.
 * On one line wins a region, then the longer range, then the syntax one; of two crossing ranges the earlier one.
 */
function nest(ranges: CodeFoldingRange[]): CodeFoldingRange[] {
	const indexed = ranges.map((range, order) => ({ range, order }));
	indexed.sort((a, b) => a.range.start - b.range.start
		|| +(b.range.kind === 'region') - +(a.range.kind === 'region')
		|| b.range.end - a.range.end
		|| a.order - b.order);

	const result: CodeFoldingRange[] = [];
	const stack: CodeFoldingRange[] = [];
	for (const { range } of indexed) {
		while (stack.length && stack[stack.length - 1].end < range.start) stack.pop();
		const parent = stack[stack.length - 1];
		if (parent && (parent.start === range.start || range.end > parent.end)) continue;
		stack.push(range);
		result.push(range);
	}
	return result;
}
